import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";

export interface QuestionSolution {
  question: {
    id: string;
    questionTitle: string;
    type: string;
    parentName?: string;
    difficultyLevel?: string;
    traitReference?: string;
    traitName?: string | null;

    version: {
      questionDescription?: string;

      solution?:
        | {
            options?: Record<
              string,
              {
                value: string;
                weightage: number;
              }
            >;

            correctOptions?: string[] | null;
            answerExplanation?: string;

            // Numeric fields in question version
            correctValue?: number;
            tolerance?: number;
            unit?: string;
          }
        | string[];

      marks?: number;
      duration?: number;
    };
  };

  resource?: {
    descriptionImages?: string[] | null;
    images?: string[];
    text?: string[];
  };

  solution?: string | null;
  actualAnswer?: string | null;

  studentOptions?: string[];
  correctOptions?: string[];

  // Blanks
  studentBlankOptions?: Record<string, string[]>;
  correctBlankOptions?: Record<string, string[]>;

  // Match the Following
  matching?: Record<string, string>;
  correctMatching?: Record<string, string>;

  // Numeric response fields
  numericValue?: number | null;
  correctValue?: number | null;
  tolerance?: number | null;

  feedback?: string | null;

  marks: number;
  status: "PASS" | "FAIL" | string;

  time?: number | null;
  submittedOn?: string | null;
  remarks?: string | null;
}

export interface Section {
  sectionName: string;
  sectionTotalMarks: number;
  sectionScoredMarks: number;
  totalQuestions: number;
  questionsAttended: number;
  passedQuestions: number;
  studentAnswerResponseList: QuestionSolution[];
}

export interface GreScoreReport {
  candidateName?: string;
  testDate?: string;
  mockTestName?: string;
  verbalScore?: number;
  quantitativeScore?: number;
  combinedScore?: number;
  awaScore?: number;
  verbalPath?: string[];
  quantitativePath?: string[];
  sectionPerformanceList?: Array<{
    sectionName: string;
    moduleDifficulty?: string;
    questionsAttempted?: number;
    correct?: number;
    incorrect?: number;
    unanswered?: number;
    accuracyPercentage?: number;
    timeTaken?: number | null;
    averageTimePerQuestion?: number | null;
  }>;
}

export interface QuizResult {
  quizId?: string;
  quizName: string;

  studentReference?: string;
  studentName: string;
  studentEmail?: string;

  marks: number;
  totalMarks: number;
  percentage: number;

  status?: string | null;

  totalQuestions: number;
  attended: number;
  totalCorrectAnswered: number;
  totalIncorrectAnswered?: number;

  duration: number;
  totalTime: number;
  timeTaken?: number;

  attempts?: number | null;
  attemptedOn?: string | null;

  sectionQuestionSolutionsSet: Section[];

  isActualAnswer: boolean;
  isShowResult: boolean;

  malPractice: boolean;
  malPracticeCount?: number;
  totalMalPracticeCount?: number;

  passPercentage?: number | null;
  quizType?: string;
  roleStudentData?: {
    role: string;
    totalScoredTraitMarks: number;
    totalTrailWeight: number;
    rolePercentage: number;
  }[];

  bestFit?: {
    role: string;
    percentage: number;
    roleDescription?:string;
  }[];

  traitStudentData?: Record<
    string,
    {
      name: string;
      traitTotalMarks: number;
      traitScoredMarks: number;
      traitPercentage: number;
    }
  >;
  greScoreReport?: GreScoreReport | null;
}

interface GetStudentQuizResultResponse extends APIResponse {
  response: QuizResult[];
}

export const ResultApiSlice = createApi({
  reducerPath: "resultApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getStudentQuizResult: builder.query<
      GetStudentQuizResultResponse,
      {
        studentId: string;
        batchId?: string;
        assessmentId: string;
        quizCategoryType?: string;
      }
    >({
      query: ({ studentId, batchId, assessmentId, quizCategoryType }) => ({
        url:
          quizCategoryType === "assessment"
            ? `student/get-student-assessment-response?studentId=${studentId}&batchId=${batchId}&assessmentId=${assessmentId}`
            : `student/get-student-quiz-response?quizId=${assessmentId}${
                studentId ? `&studentId=${studentId}` : ""
              }`,
        method: "GET",
      }),
    }),
  }),
});

export const { useGetStudentQuizResultQuery } = ResultApiSlice;
